import React, { useEffect, useState, useCallback } from 'react';
import { UserCheck, Phone, Search, Filter, Download, X, Trash2, AlertTriangle } from 'react-feather';
import { Card, CardBody, CardHeader, Table, Badge, Alert, Input, InputGroup, InputGroupText, Button, Row, Col, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label } from 'reactstrap';
import { H5, H6 } from '../../../AbstractElements';
import { getUsers, deleteUserByUserId } from '../../../api/userCheckIn';
import eventApi from '../../../api/CheckInEvent';
import { exportCheckinUsers } from '../../../api/userQuiz';
import { toast } from 'react-toastify';
import Pagination from '../UserQuiz/Pagination';

const MyCurrencies = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState(''); // Local state for input
  const [searchType, setSearchType] = useState('name'); // 'name', 'userId', 'phone'
  const [eventId, setEventId] = useState('');
  const [eventOptions, setEventOptions] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  
  // Export states
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportEventId, setExportEventId] = useState(''); // Event ID for export (can be different from filter)
  const [exportOptions, setExportOptions] = useState({
    includeRegistrations: true,
    includeCheckins: true,
    filterType: '',
    filename: ''
  });

  // Delete states
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Load events on mount
  useEffect(() => {
    const loadEvents = async () => {
      try {
        setLoadingEvents(true);
        const response = await eventApi.getAll();
        
        // Handle different response structures
        let eventsData = [];
        if (response && response.success && response.data && response.data.events) {
          eventsData = response.data.events;
        } else if (Array.isArray(response)) {
          eventsData = response;
        } else if (response && Array.isArray(response.data)) {
          eventsData = response.data;
        } else if (response && Array.isArray(response.data?.events)) {
          eventsData = response.data.events;
        }

        const options = eventsData
          .map((event) => {
            const id = event.id || event.eventId;
            if (!id) return null;
            const name = event.name || event.title || `Event #${id}`;
            return { id, name };
          })
          .filter(Boolean)
          .sort((a, b) => a.name.localeCompare(b.name)); // Sort by name

        setEventOptions(options);
      } catch (err) {
        console.error('Error loading events:', err);
        setEventOptions([]);
      } finally {
        setLoadingEvents(false);
      }
    };

    loadEvents();
  }, []);

  // Fetch users from API
  const fetchUsersData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit
      };

      // Add search parameter based on searchType
      if (searchTerm) {
        const trimmedSearch = searchTerm.trim();
        if (searchType === 'name') {
          params.name = trimmedSearch;
        } else if (searchType === 'userId') {
          params.userId = trimmedSearch;
        } else if (searchType === 'phone') {
          params.phone = trimmedSearch;
        }
      }

      // Add eventId parameter if eventId exists
      if (eventId) {
        params.eventId = parseInt(eventId);
      }

      const response = await getUsers(params);
      
      // Handle response structure
      let userData = [];
      let paginationData = pagination;

      if (response?.success) {
        // Standard response format
        userData = response.data?.users || response.data || [];
        paginationData = response.data?.pagination || pagination;
      } else if (response?.data?.users && Array.isArray(response.data.users)) {
        userData = response.data.users;
        paginationData = response.data.pagination || pagination;
      } else if (response?.data && Array.isArray(response.data)) {
        userData = response.data;
      } else if (response?.users && Array.isArray(response.users)) {
        userData = response.users;
      } else if (Array.isArray(response)) {
        userData = response;
      }
      
      setUsers(userData);
      setPagination(prev => ({
        ...prev,
        ...paginationData
      }));
      setError(null);
    } catch (err) {
      console.error('Error fetching users in MyCurrencies:', err);
      setError(err.message || 'Failed to load users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, searchTerm, searchType, eventId]);

  // Load users when dependencies change
  useEffect(() => {
    fetchUsersData();
  }, [pagination.page, pagination.limit, searchTerm, searchType, eventId]);

  // Handle search
  const handleSearch = () => {
    setSearchTerm(searchInput);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to page 1 when searching
  };

  // Handle search on Enter key
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    setSearchType('name'); // Reset về tìm kiếm theo tên
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Handle event change
  const handleEventChange = (value) => {
    setEventId(value);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to page 1 when changing event
    // fetchUsersData will be called automatically via useEffect
  };

  // Pagination handlers
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleLimitChange = (newLimit) => {
    setPagination(prev => ({ ...prev, limit: parseInt(newLimit), page: 1 }));
  };

  // Initialize export eventId when modal opens
  useEffect(() => {
    if (exportModalOpen) {
      setExportEventId(eventId || ''); // Use current filter eventId if available
    }
  }, [exportModalOpen, eventId]);

  // Handle export
  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {
        includeRegistrations: exportOptions.includeRegistrations,
        includeCheckins: exportOptions.includeCheckins
      };

      // Only add eventId if selected (if empty, export all users)
      if (exportEventId) {
        params.eventId = parseInt(exportEventId);
      }

      if (exportOptions.filterType) {
        params.filterType = exportOptions.filterType;
      }

      if (exportOptions.filename) {
        params.filename = exportOptions.filename;
      }

      await exportCheckinUsers(params);
      toast.success('✅ Export thành công! File đã được tải về máy.');
      setExportModalOpen(false);
    } catch (error) {
      console.error('Export error details:', error);
      
      let errorMessage = error.message || 'Có lỗi xảy ra';
      
      if (error.status) {
        errorMessage += ` (Status: ${error.status})`;
        
        if (error.status === 404) {
          errorMessage += ' - Endpoint không tồn tại. Vui lòng kiểm tra lại API endpoint.';
        } else if (error.status === 401 || error.status === 403) {
          errorMessage += ' - Không có quyền truy cập. Vui lòng kiểm tra lại authentication.';
        } else if (error.status >= 500) {
          errorMessage += ' - Lỗi server. Vui lòng thử lại sau.';
        }
      }
      
      if (error.contentType) {
        errorMessage += ` [Content-Type: ${error.contentType}]`;
      }
      
      toast.error(`❌ Lỗi khi export: ${errorMessage}`);
      
      if (error.htmlPreview) {
        console.error('HTML Response Preview:', error.htmlPreview);
      }
    } finally {
      setExporting(false);
    }
  };

  // Handle export options change
  const handleExportOptionChange = (key, value) => {
    setExportOptions(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Reset export options
  const handleResetExportOptions = () => {
    setExportEventId(eventId || '');
    setExportOptions({
      includeRegistrations: true,
      includeCheckins: true,
      filterType: '',
      filename: ''
    });
  };

  // Handle delete user
  const handleDeleteClick = (userId, userName) => {
    setUserToDelete({ userId, userName });
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;

    setDeleting(true);
    try {
      await deleteUserByUserId(userToDelete.userId);
      toast.success(`✅ Đã xóa user "${userToDelete.userName || userToDelete.userId}" thành công!`);
      setDeleteModalOpen(false);
      setUserToDelete(null);
      
      // Refresh user list
      await fetchUsersData();
    } catch (error) {
      toast.error('❌ Lỗi khi xóa user: ' + (error.message || 'Có lỗi xảy ra'));
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setUserToDelete(null);
  };

  const tableHeader = ['User Info', 'Phone', 'Pharmacy', 'Address', 'Follow Status', 'Action'];

  return (
    <Card>
      <CardHeader className='card-no-border'>
        <div className='header-top d-flex justify-content-between align-items-center'>
          <div>
            <H5>User Check-In Management</H5>
            <Badge color='light-primary' className='text-primary'>
              Total: {pagination.total || users.length} users
            </Badge>
          </div>
          <Button 
            color='success' 
            onClick={() => setExportModalOpen(true)}
            disabled={loading}
          >
            <Download size={16} className='me-1' />
            Export Excel
          </Button>
        </div>
        <div className='mt-3'>
          <Row className='g-3'>
            <Col md="4">
              <label className='small fw-bold mb-1 d-block'>
                <Filter size={14} className='me-1' />
                Event
              </label>
              <Input
                type='select'
                value={eventId}
                onChange={(e) => handleEventChange(e.target.value)}
                disabled={loadingEvents || loading}
              >
                <option value=''>Tất cả events</option>
                {eventOptions.map((event) => (
                  <option key={event.id} value={event.id}>
                    {event.name}
                  </option>
                ))}
              </Input>
              {loadingEvents && (
                <small className='text-muted d-block mt-1'>Đang tải events...</small>
              )}
            </Col>
            <Col md="8">
              <label className='small fw-bold mb-1 d-block'>
                <Search size={14} className='me-1' />
                Tìm kiếm
              </label>
              <InputGroup>
                <InputGroupText>
                  <Search size={16} />
                </InputGroupText>
                <Input
                  type='select'
                  value={searchType}
                  onChange={(e) => setSearchType(e.target.value)}
                  style={{ width: '120px', flexShrink: 0 }}
                >
                  <option value='name'>Tên</option>
                  <option value='phone'>SĐT</option>
                  <option value='userId'>User ID</option>
                </Input>
                <Input
                  type='text'
                  placeholder={`Tìm kiếm theo ${searchType === 'name' ? 'tên' : searchType === 'phone' ? 'SĐT' : 'User ID'}...`}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                />
                <Button color='primary' onClick={handleSearch} disabled={loading}>
                  Tìm kiếm
                </Button>
                {searchTerm && (
                  <Button color='secondary' outline onClick={handleClearSearch} disabled={loading}>
                    Xóa
                  </Button>
                )}
              </InputGroup>
              {searchTerm && (
                <div className='mt-2'>
                  <small className='text-muted'>
                    Đang tìm kiếm theo <strong>{searchType === 'name' ? 'tên' : searchType === 'phone' ? 'SĐT' : 'User ID'}</strong>: <strong>"{searchTerm}"</strong>
                  </small>
                </div>
              )}
            </Col>
          </Row>
        </div>
      </CardHeader>
      <CardBody className='pt-0'>
        {loading ? (
          <div className='text-center p-4'>
            <div className='spinner-border text-primary' role='status'>
              <span className='visually-hidden'>Loading...</span>
            </div>
          </div>
        ) : error ? (
          <Alert color='danger'>
            <i className='fa fa-exclamation-triangle me-2'></i>
            {error}
          </Alert>
        ) : users.length === 0 ? (
          searchTerm ? (
            <Alert color='warning'>
              <i className='fa fa-search me-2'></i>
              Không tìm thấy kết quả phù hợp với "{searchTerm}"
            </Alert>
          ) : (
            <Alert color='info'>
              <i className='fa fa-info-circle me-2'></i>
              No users found. Please check your API connection.
            </Alert>
          )
        ) : (
          <>
            <div className='recent-table table-responsive currency-table'>
              <Table>
                <thead>
                  <tr>
                    {tableHeader.map((item, i) => (
                      <th key={i} className='f-light'>
                        {item}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {users.map((user, i) => (
                    <tr key={user.userId || i}>
                      <td>
                        <div className='d-flex align-items-center gap-2'>
                          {user.avatar ? (
                            <img 
                              className='img-40 rounded-circle' 
                              src={user.avatar} 
                              alt={user.name}
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                              style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                            />
                          ) : (
                            <div className='bg-light-primary d-flex align-items-center justify-content-center rounded-circle' style={{ width: '40px', height: '40px', minWidth: '40px' }}>
                            <UserCheck size={20} className='text-primary' />
                          </div>
                          )}
                          <div>
                            <H6 attrH6={{ className: 'f-14 mb-0 f-w-500' }}>{user.name || 'Unknown'}</H6>
                            <span className='f-light f-12'>ID: {user.userId?.substring(0, 15)}...</span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className='d-flex align-items-center gap-1'>
                          <Phone size={14} className='text-muted' />
                          <span className='f-14'>{user.phone || 'N/A'}</span>
                        </div>
                      </td>
                      <td>
                        <span className='f-14 f-w-500'>{user.pharmacyName || 'Not set'}</span>
                      </td>
                      <td>
                        {user.address && user.address.length > 0 ? (
                          <div className='f-12'>
                            <span className='d-block'>{user.address[0].city || ''}</span>
                            <span className='f-light'>{user.address[0].district || ''}</span>
                          </div>
                        ) : (
                          <span className='f-light'>No address</span>
                        )}
                      </td>
                      <td>
                        <Badge color={user.followedOA ? 'light-success' : 'light-secondary'}>
                          {user.followedOA ? (
                            <><UserCheck size={12} className='me-1' />Followed</>
                          ) : (
                            'Not Follow'
                          )}
                        </Badge>
                      </td>
                      <td>
                        <div className='d-flex gap-2'>
                          <button 
                            className='btn badge-light-primary btn-sm'
                          >
                            View Details
                          </button>
                          <button 
                            className='btn badge-light-danger btn-sm'
                            onClick={() => handleDeleteClick(user.userId, user.name || user.userId)}
                          >
                            <Trash2 size={14} className='me-1' />
                            Xóa
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            
            {/* Pagination */}
            {pagination.totalPages > 0 && (
              <Pagination
                pagination={pagination}
                onPageChange={handlePageChange}
                onLimitChange={handleLimitChange}
              />
            )}
          </>
        )}
      </CardBody>

      {/* Export Modal */}
      <Modal isOpen={exportModalOpen} toggle={() => setExportModalOpen(false)} size="md">
        <ModalHeader toggle={() => setExportModalOpen(false)}>
          <Download size={20} className="me-2" />
          Export Checkin Users to Excel
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label for="exportEventId">Event (tùy chọn)</Label>
              <Input
                type="select"
                id="exportEventId"
                value={exportEventId}
                onChange={(e) => setExportEventId(e.target.value)}
                disabled={loadingEvents}
              >
                <option value="">Tất cả events (Export tất cả users)</option>
                {eventOptions.map((event) => (
                  <option key={event.id} value={event.id}>
                    {event.name}
                  </option>
                ))}
              </Input>
              {loadingEvents && (
                <small className="text-muted d-block mt-1">Đang tải events...</small>
              )}
              {!exportEventId && (
                <small className="text-info d-block mt-1">
                  <i className="fa fa-info-circle me-1"></i>
                  Sẽ export tất cả users từ tất cả events
                </small>
              )}
              {eventId && exportEventId === eventId && (
                <small className="text-info d-block mt-1">
                  <i className="fa fa-info-circle me-1"></i>
                  Đang sử dụng event từ filter: <strong>{eventOptions.find(e => e.id.toString() === eventId)?.name || `Event ID: ${eventId}`}</strong>
                </small>
              )}
            </FormGroup>

              <FormGroup>
                <div className="form-check form-switch">
                  <Input
                    type="checkbox"
                    className="form-check-input"
                    id="includeRegistrations"
                    checked={exportOptions.includeRegistrations}
                    onChange={(e) => handleExportOptionChange('includeRegistrations', e.target.checked)}
                  />
                  <Label className="form-check-label" for="includeRegistrations">
                    Bao gồm dữ liệu đăng ký (Registrations)
                  </Label>
                </div>
              </FormGroup>

              <FormGroup>
                <div className="form-check form-switch">
                  <Input
                    type="checkbox"
                    className="form-check-input"
                    id="includeCheckins"
                    checked={exportOptions.includeCheckins}
                    onChange={(e) => handleExportOptionChange('includeCheckins', e.target.checked)}
                  />
                  <Label className="form-check-label" for="includeCheckins">
                    Bao gồm dữ liệu checkin
                  </Label>
                </div>
              </FormGroup>

              <FormGroup>
                <Label for="filterType">Filter Type (tùy chọn)</Label>
                <Input
                  type="select"
                  id="filterType"
                  value={exportOptions.filterType}
                  onChange={(e) => handleExportOptionChange('filterType', e.target.value)}
                >
                  <option value="">Tất cả users</option>
                  <option value="checkin">Chỉ users đã checkin</option>
                </Input>
                <small className="text-muted">Chọn "checkin" để chỉ export users đã checkin event</small>
              </FormGroup>

              <FormGroup>
                <Label for="filename">Tên file tùy chỉnh (tùy chọn)</Label>
                <Input
                  type="text"
                  id="filename"
                  placeholder="Để trống để tự động tạo tên"
                  value={exportOptions.filename}
                  onChange={(e) => handleExportOptionChange('filename', e.target.value)}
                />
                <small className="text-muted">Nếu không nhập, tên file sẽ được tạo tự động</small>
              </FormGroup>
            </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={handleResetExportOptions} disabled={exporting}>
            <X size={14} className="me-1" />
            Reset
          </Button>
          <Button color="primary" onClick={handleExport} disabled={exporting}>
            <Download size={14} className="me-1" />
            {exporting ? 'Đang export...' : 'Export Excel'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModalOpen} toggle={handleCancelDelete} centered>
        <ModalHeader toggle={handleCancelDelete} className="bg-danger text-white">
          <AlertTriangle size={20} className="me-2" />
          Xác nhận xóa user
        </ModalHeader>
        <ModalBody className="p-4">
          <div className="d-flex align-items-center mb-3">
            <div className="bg-danger bg-opacity-10 rounded-circle p-3 me-3">
              <AlertTriangle size={32} className="text-danger" />
            </div>
            <div>
              <h5 className="mb-1">Bạn có chắc chắn muốn xóa?</h5>
              <p className="mb-0 text-muted">
                User: <strong>{userToDelete?.userName || userToDelete?.userId}</strong>
              </p>
              <p className="mb-0 text-muted small mt-2">
                ID: <code className="text-danger">{userToDelete?.userId}</code>
              </p>
            </div>
          </div>
          <div className="alert alert-warning mb-0">
            <strong>⚠️ Cảnh báo:</strong> Hành động này không thể hoàn tác. Tất cả dữ liệu liên quan đến user này sẽ bị xóa vĩnh viễn.
          </div>
        </ModalBody>
        <ModalFooter className="bg-light">
          <Button 
            color="secondary" 
            onClick={handleCancelDelete}
            disabled={deleting}
          >
            Hủy
          </Button>
          <Button 
            color="danger" 
            onClick={handleConfirmDelete}
            disabled={deleting}
          >
            {deleting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Đang xóa...
              </>
            ) : (
              <>
                <Trash2 size={16} className="me-1" />
                Xóa
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </Card>
  );
};

export default MyCurrencies;
